import React, { useState, useMemo } from 'react';
import DynamicTab from './components/generic/DynamicTab';
import WelcomePage from './components/WelcomePage';
import { useTenant, TenantProvider } from './components/TenantContext';
import { Control } from './type';
import { ENTITY_NAMES } from './constants';
import { toTabKey } from './utils/toTabKey';

// Defines the order of entities for the tabs.
const ORDERED_TABS_CONFIG = [
  { entityName: ENTITY_NAMES.STUDENT_INFORMATION },
  { entityName: ENTITY_NAMES.ACADEMIC_SCORES },
  { entityName: ENTITY_NAMES.FAMILY_INFORMATION },
  { entityName: ENTITY_NAMES.PARENT_FINANCES },
  { entityName: ENTITY_NAMES.STUDENT_FINANCES },
  { entityName: ENTITY_NAMES.SUMMARY },
];

const AppContent: React.FC = () => {
  const { tenantConfig, entitiesData, loading } = useTenant();
  const [showCalculator, setShowCalculator] = useState(false);
  const [formValues, setFormValues] = useState<Record<string, string>>({});
  const [errors] = useState<Record<string, string>>({});
  const [studentRequiredControls, setStudentRequiredControls] = useState<Control[]>([]);

  const availableTabs = useMemo(() => {
    if (!entitiesData) {
      return [];
    }
    const availableEntityNames = new Set(entitiesData.map(e => e.name));
    return ORDERED_TABS_CONFIG
      .filter(config => availableEntityNames.has(config.entityName))
      .map(config => toTabKey(config.entityName));
  }, [entitiesData]);

  const { calculateDerivedAttributesForEntity, getEntityIdByName } = useTenant();

  const handleStart = async () => {
    try {
      // Call derived attributes for StudentInformation and Estimate sequentially
      const studentEntityId = getEntityIdByName('StudentInformation');
      const estimateEntityId = getEntityIdByName('Estimate');

      if (studentEntityId) {
        await calculateDerivedAttributesForEntity(studentEntityId);
      }

      if (estimateEntityId) {
        await calculateDerivedAttributesForEntity(estimateEntityId);
      }
    } catch (err) {
      console.error('handleStart: calculateDerivedAttributesForEntity failed', err);
    } finally {
      setShowCalculator(true);
      // mark that the user has entered the calculator
      try { localStorage.setItem('ENTERED_CALCULATOR', 'true'); } catch (e) { console.warn('localStorage unavailable', e); }
    }
  };

  // When WelcomePage is shown, mark it as visited so logo can navigate back
  React.useEffect(() => {
    if (!showCalculator) {
      try { localStorage.setItem('VISITED_WELCOME', 'true'); } catch (e) { console.warn('localStorage unavailable', e); }
    }
  }, [showCalculator]);

  const updateStudentRequiredControls = (controls: Control[]) => {
    setStudentRequiredControls(controls);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="flex flex-col items-center space-y-4">
          <img
            src="/assets/images/bannerhcrc.png"
            alt="Loading - HCRC Banner"
            className="max-w-md w-full h-auto animate-pulse"
          />
        </div>
      </div>
    );
  }

  if (!tenantConfig) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-sm text-gray-500">
          <img
            src="/assets/images/bannerhcrc.png"
            alt="Loading - HCRC Banner"
            className="max-w-md w-full h-auto animate-pulse"
          />        </div>
      </div>
    );
  }

  return (
    <div className='container-fluid'>
      <div className='text-left'>
        {showCalculator ? (
          <DynamicTab
            tabs={availableTabs}
            controls={studentRequiredControls}
            formValues={formValues}
            setFormValues={setFormValues}
            errors={errors}
            entitiesData={entitiesData ?? []}
            updateStudentRequiredControls={updateStudentRequiredControls}
            onStartCalculator={handleStart}
          />
        ) : (
          <WelcomePage
            controls={entitiesData ?? []}
            onStartClick={handleStart}
          />
        )}
      </div>
    </div>
  );
};

const App: React.FC = () => {
  return (
    <TenantProvider>
      <AppContent />
    </TenantProvider>
  );
};

export default App;
